import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError, API } from "@/auth";
import { LHSLogo } from "@/logos";
import { Loader2, Fingerprint, ShieldCheck, Wifi, Download, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [appCfg, setAppCfg] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");

  const loadCaptcha = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/captcha`);
      setCaptcha(data);
      setAnswer("");
    } catch (e) { console.warn("captcha load failed", e?.message); }
  };

  useEffect(() => {
    axios.get(`${API}/app/config`).then(r => setAppCfg(r.data)).catch(() => {});
    loadCaptcha();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password, {
        captcha_a: captcha?.a,
        captcha_b: captcha?.b,
        captcha_answer: parseInt(answer, 10),
      });
      nav("/");
    } catch (err) {
      setError(formatApiError(err));
      loadCaptcha();
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-black bg-grid relative overflow-hidden flex items-center justify-center px-4 py-8">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
           style={{ background: "radial-gradient(circle, rgba(255,107,0,.18), transparent 60%)" }} />
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full"
           style={{ background: "radial-gradient(circle, rgba(255,107,0,.14), transparent 60%)" }} />
      <div className="bg-noise absolute inset-0 pointer-events-none" />

      <div className="absolute top-4 right-4 flex items-center gap-2" data-testid="lang-switcher">
        {["fr","en","ar"].map(l => (
          <button key={l} data-testid={`lang-${l}`} onClick={() => setLanguage(l)}
            className={`text-[11px] font-mono-tech px-2 py-1 rounded border ${i18n.language === l ? "border-orange-500 text-orange-400" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><LHSLogo size={72} /></div>
        <div className="card-lhs p-6 sm:p-8" data-testid="login-card">
          <h1 className="font-display text-2xl font-extrabold text-white uppercase tracking-wider mb-1">{t("login_title")}</h1>
          <p className="text-sm text-zinc-400 mb-6">{t("login_subtitle")} <span className="text-orange-500 font-semibold">LHSunlocker</span></p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 font-mono-tech">{t("email")}</label>
              <input data-testid="login-email" type="email" required className="input-lhs"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="technicien@atelier.com" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 font-mono-tech">{t("password")}</label>
              <input data-testid="login-password" type="password" required className="input-lhs"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {captcha && (
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-mono-tech flex items-center gap-2">
                  Vérification anti-robot
                  <button type="button" onClick={loadCaptcha} className="text-zinc-500 hover:text-orange-400"><RefreshCw className="w-3 h-3" /></button>
                </label>
                <div className="flex gap-2 items-center">
                  <span data-testid="captcha-question" className="font-mono-tech text-lg text-orange-400 bg-[#0C0C10] border border-zinc-800 px-3 py-2 rounded-lg select-none">
                    {captcha.question}
                  </span>
                  <input data-testid="captcha-answer" type="number" required className="input-lhs flex-1"
                    value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Réponse" />
                </div>
              </div>
            )}
            {error && <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit" disabled={loading} className="btn-orange w-full rounded-lg py-3 flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
              {t("login")}
            </button>
          </form>

          <div className="flex items-center justify-between mt-4">
            <Link data-testid="link-forgot" to="/forgot" className="text-xs text-zinc-400 hover:text-orange-400">Mot de passe oublié ?</Link>
            <Link data-testid="link-register" to="/register" className="text-xs text-orange-500 font-semibold hover:text-orange-400">{t("login_create")}</Link>
          </div>

          <div className="flex items-center gap-4 mt-6 text-[11px] text-zinc-500 font-mono-tech flex-wrap">
            <span className="flex items-center gap-1"><span className="pulse-dot" /> Server: online</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-orange-500" /> TLS 1.3</span>
            <span className="flex items-center gap-1"><Wifi className="w-3 h-3 text-orange-500" /> v{appCfg?.app_version || "1.0.0"}</span>
          </div>

          {appCfg?.app_download_url && (
            <a data-testid="download-client-btn"
              href={appCfg.app_download_url.startsWith("http") ? appCfg.app_download_url : `${API}${appCfg.app_download_url.replace(/^\/api/, "")}`}
              target="_blank" rel="noopener noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-2 border border-orange-500/40 text-orange-400 hover:bg-orange-500/10 rounded-lg py-2.5 text-xs uppercase tracking-wider transition">
              <Download className="w-4 h-4" />
              {t("download_client")}
              {appCfg.installer_filename && <span className="text-[10px] text-zinc-500 ml-1">· {(appCfg.installer_size_bytes/(1024*1024)).toFixed(1)}MB</span>}
            </a>
          )}
        </div>
        <p className="text-center text-[11px] text-zinc-600 mt-6 font-mono-tech">© LHSunlocker · Plateforme GSM Professionnelle</p>
      </div>
    </div>
  );
}
